/**
 * NewsletterImageDropzone — Feb 2026
 * ==================================
 *
 * Tiny drag-and-drop image uploader for the Newsletter Operations Card.
 * Drop a PNG/JPG/WEBP (≤ 5 MB), it ships to
 *   POST /api/admin/newsletter/upload-image
 * and the parent gets the resulting `/api/static/newsletter-assets/…`
 * URL via `onUploaded(url)`. The same component also accepts a manual
 * URL paste via a small input below the drop area.
 *
 * Props:
 *   value      — current URL (string)
 *   onUploaded — callback (newUrl: string) => void
 *   compact    — true → smaller variant for the per-post image fields
 *   testIdBase — prefix for data-testid hooks
 */
import React, { useCallback, useRef, useState } from 'react';
import { ImagePlus, Loader2, X, Upload } from 'lucide-react';
import { apiClient } from '../../api/client';

const ACCEPT = 'image/png,image/jpeg,image/jpg,image/webp';
const MAX_MB = 5;

const NewsletterImageDropzone = ({
  value,
  onUploaded,
  compact = false,
  testIdBase = 'newsletter-img',
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`Too large — keep it under ${MAX_MB} MB.`);
      return;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      setError('Only JPG, PNG, or WEBP images are accepted.');
      return;
    }
    setError('');
    setUploading(true);
    try {
      // We bypass `apiClient.request` because that helper sets a JSON
      // content-type; for multipart we need the browser to set the
      // boundary itself. Use the raw fetch with the same auth header.
      const token = (
        localStorage.getItem('hibff_auth_token')
        || sessionStorage.getItem('hibff_auth_token')
      );
      const fd = new FormData();
      fd.append('file', file);
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const resp = await fetch(`${base}/api/admin/newsletter/upload-image`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail?.detail || `Upload failed (HTTP ${resp.status})`);
      }
      const data = await resp.json();
      if (data?.url) {
        onUploaded(data.url);
      } else {
        throw new Error('Upload succeeded but no URL was returned.');
      }
    } catch (e) {
      setError(e?.message || 'Upload failed — try again.');
    } finally {
      setUploading(false);
    }
  }, [onUploaded]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const onPick = (e) => {
    const file = e.target?.files?.[0];
    if (file) handleFile(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const previewUrl = value
    ? (value.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${value}` : value)
    : null;

  return (
    <div className="w-full" data-testid={`${testIdBase}-dropzone`}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`w-full flex items-center gap-3 ${compact ? 'p-2.5' : 'p-3.5'} rounded-lg border-2 border-dashed transition cursor-pointer text-left`}
        style={{
          borderColor: dragOver ? '#F5831F' : '#D1D5DB',
          background: dragOver ? '#FFF7ED' : '#FAFAFA',
        }}
        aria-label="Upload image"
        data-testid={`${testIdBase}-drop-button`}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="preview"
            className={`${compact ? 'w-10 h-10' : 'w-14 h-14'} rounded-md object-cover flex-shrink-0`}
            data-testid={`${testIdBase}-preview`}
          />
        ) : (
          <div
            className={`${compact ? 'w-10 h-10' : 'w-14 h-14'} rounded-md flex items-center justify-center flex-shrink-0`}
            style={{ background: '#F3F4F6', color: '#9CA3AF' }}
          >
            <ImagePlus size={compact ? 16 : 22} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className={`${compact ? 'text-[11px]' : 'text-xs'} font-semibold text-gray-700`}>
            {uploading ? 'Uploading…' : (value ? 'Replace image' : 'Drop image here, or click to pick')}
          </div>
          <div className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-gray-500 truncate`}>
            {value
              ? value.replace(/^.*\//, '')
              : `JPG · PNG · WEBP · up to ${MAX_MB} MB · sanitised on upload`}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {uploading ? (
            <Loader2 size={compact ? 14 : 16} className="animate-spin" style={{ color: '#F5831F' }} />
          ) : (
            <Upload size={compact ? 14 : 16} style={{ color: '#9CA3AF' }} />
          )}
          {value && !uploading && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onUploaded(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onUploaded(''); } }}
              className="w-6 h-6 rounded-full flex items-center justify-center hover:bg-red-50 text-red-500 cursor-pointer"
              aria-label="Clear image"
              data-testid={`${testIdBase}-clear`}
            >
              <X size={12} />
            </span>
          )}
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={onPick}
        data-testid={`${testIdBase}-file-input`}
      />
      {error && (
        <div className="mt-1.5 text-[11px] text-red-600" data-testid={`${testIdBase}-error`}>
          {error}
        </div>
      )}
    </div>
  );
};

export default NewsletterImageDropzone;
